import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icons from "../components/icons";

import {
  Check,
  ChevronLeft,
  ChevronRight,
  Info,
  MoreVertical,
  X,
} from "lucide-react";
import { authFetch } from "../lib/api";
const noteCategories = [
  {
    key: "meeting",
    label: "Meetings",
    dot: "bg-[#FFAE00]",
    text: "text-[#D18B00]",
    bg: "bg-[#FFF8E1]",
    keywords: [
      "meeting",
      "call",
      "discussion",
      "discuss",
      "stakeholder",
      "client",
      "sync",
      "demo",
      "presentation",
      "review meeting",
      "standup",
      "interview",
      "appointment",
      "agenda",
    ],
  },
  {
    key: "work",
    label: "Work",
    dot: "bg-[#4379FE]",
    text: "text-[#4379FE]",
    bg: "bg-[#EEF2FF]",
    keywords: [
      "project",
      "task",
      "design",
      "sprint",
      "development",
      "bug",
      "dashboard",
      "kanban",
      "report",
      "timesheet",
      "deployment",
      "testing",
      "api",
      "review",
      "feature",
      "ticket",
      "client requirement",
    ],
  },
  {
    key: "personal",
    label: "Personal",
    dot: "bg-[#22C55E]",
    text: "text-[#22C55E]",
    bg: "bg-[#EAF7EE]",
    keywords: [
      "buy",
      "doctor",
      "birthday",
      "family",
      "vacation",
      "travel",
      "shopping",
      "bank",
      "renew",
      "pay bill",
      "friend",
      "exercise",
      "home",
      "grocery",
    ],
  },
];

const generalCategory = {
  key: "general",
  label: "General",
  dot: "bg-gray-400",
  text: "text-gray-600",
  bg: "bg-gray-50",
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// This router should be mounted in the backend as: app.use("/api/my-work", router)
const MY_WORK_TASKS_ENDPOINT = "/api/tasks/calendar";
const formatDateKey = (date) => {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDisplayDate = (dateKey) => {
  const [yyyy, mm, dd] = dateKey.split("-").map(Number);
  return `${String(dd).padStart(2, "0")} ${months[mm - 1]} ${yyyy}`;
};

const formatTime = (isoDate) =>
  new Date(isoDate).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const getCategoryForText = (text) => {
  const normalized = text.toLowerCase();

  return (
    noteCategories.find((category) =>
      category.keywords.some((keyword) => {
        const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return new RegExp(`\\b${escaped}\\b`, "i").test(normalized);
      }),
    ) || generalCategory
  );
};

const MyWork = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesError, setNotesError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [activeNoteMenu, setActiveNoteMenu] = useState(null);
const [showLegend, setShowLegend] = useState(false);
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    const loadNotes = async () => {
      try {
        setNotesLoading(true);
        const res = await authFetch("/api/notes");
        const data = await res.json();
        if (data.success) {
          setNotes(data.data);
        } else {
          setNotesError(data.message || "Failed to load notes.");
        }
      } catch (err) {
        setNotesError("Failed to load notes.");
      } finally {
        setNotesLoading(false);
      }
    };
    loadNotes();
  }, []);

  useEffect(() => {
    fetch("http://localhost:5000/api/dashboard/projects")
      .then((res) => res.json())
      .then((res) => {
        const projects = res.data || [];
        const allTasks = [];

        projects.forEach((project) => {
          project.lists?.forEach((list) => {
            list.tasks?.forEach((task) => {
              if (task.endDate) {
                allTasks.push({
                  date: formatDateKey(task.endDate),
                  title: `${project.name} | ${task.name}`,
                });
              }
            });
          });
        });

        setTasks(allTasks);
      })
      .catch(() => {});
  }, []);

  const days = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const start = firstDay === 0 ? 6 : firstDay - 1;
    const calendarDays = [];

    for (let i = 0; i < start; i++) {
      calendarDays.push({ day: null, current: false, dateKey: `blank-${i}` });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendarDays.push({ day, current: true, dateKey: formatDateKey(date) });
    }

    while (calendarDays.length % 7 !== 0) {
      calendarDays.push({
        day: null,
        current: false,
        dateKey: `blank-end-${calendarDays.length}`,
      });
    }

    return calendarDays;
  }, [month, year]);

  const notesByDate = useMemo(
    () =>
      notes.reduce((acc, note) => {
        acc[note.date] = acc[note.date] || [];
        acc[note.date].push(note);
        return acc;
      }, {}),
    [notes],
  );

  const selectedNotes = selectedDate ? notesByDate[selectedDate] || [] : [];

  const getTasks = (dateKey) => tasks.filter((task) => task.date === dateKey);

  const getNoteCategoriesForDate = (dateKey) => {
    const categories = (notesByDate[dateKey] || []).map((note) =>
      getCategoryForText(note.text),
    );
    const uniqueKeys = new Set();

    return categories.filter((category) => {
      if (uniqueKeys.has(category.key)) return false;
      uniqueKeys.add(category.key);
      return true;
    });
  };

 const openNotes = (dateKey) => {
  setSelectedDate(dateKey);
  setNoteText("");
  setEditingNoteId(null);
  setEditingText("");
  setActiveNoteMenu(null);
  setShowLegend(false);
};

const closeNotes = () => {
  setSelectedDate(null);
  setNoteText("");
  setEditingNoteId(null);
  setEditingText("");
  setActiveNoteMenu(null);
  setShowLegend(false);
};

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text || !selectedDate) return;

    try {
      const res = await authFetch("/api/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate, text }),
      });
      const data = await res.json();
      if (data.success) {
        setNotes((prev) => [data.data, ...prev]);
        setNoteText("");
      }
    } catch (err) {
      // optionally show a toast/error message here
    }
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note._id);
    setEditingText(note.text);
    setActiveNoteMenu(null);
  };

  const handleSaveEdit = async (noteId) => {
    const text = editingText.trim();
    if (!text) return;

    try {
      const res = await authFetch(`/api/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (data.success) {
        setNotes((prev) =>
          prev.map((note) => (note._id === noteId ? data.data : note)),
        );
        setEditingNoteId(null);
        setEditingText("");
      }
    } catch (err) {
      // optionally show a toast/error message here
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const res = await authFetch(`/api/notes/${noteId}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data.success) {
        setNotes((prev) => prev.filter((note) => note._id !== noteId));
        setActiveNoteMenu(null);
        if (editingNoteId === noteId) {
          setEditingNoteId(null);
          setEditingText("");
        }
      }
    } catch (err) {
      // optionally show a toast/error message here
    }
  };

  return (
    <div className="p-6 bg-transparent min-h-screen font-sans">
      <div className="bg-white rounded-xl px-6 py-5 shadow-sm">
        <h1 className="text-[22px] font-semibold text-gray-800">My Work</h1>
        <p className="text-sm text-gray-700 mt-3 w-fit pb-1.5 border-b-2 border-blue-500">
          Calendar
        </p>
      </div>

      <div className="mt-4 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex justify-end items-center px-6 py-3">
          <div className="flex items-center gap-4 text-gray-700">
            <button
              type="button"
              aria-label="Previous month"
              className="p-1 hover:text-blue-600"
              onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="w-[120px] text-center text-sm font-medium">
              {months[month]} {year}
            </span>
            <button
              type="button"
              aria-label="Next month"
              className="p-1 hover:text-blue-600"
              onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-y border-gray-100 text-xs text-gray-500 bg-white">
          {weekdays.map((day) => (
            <div
              key={day}
              className="border-r border-gray-100 py-2 text-center last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 border-b border-gray-200">
          {" "}
          {days.map((dayInfo) => {
            if (!dayInfo.day) {
              return (
                <div
                  key={dayInfo.dateKey}
                  className="h-[115px] border-r border-gray-100 bg-white last:border-r-0"
                />
              );
            }

            const isToday =
              dayInfo.dateKey === formatDateKey(today) && dayInfo.current;
            const dayTasks = getTasks(dayInfo.dateKey);
            const dateNoteCategories = getNoteCategoriesForDate(
              dayInfo.dateKey,
            );

            return (
              <button
                key={dayInfo.dateKey}
                type="button"
                onClick={() => openNotes(dayInfo.dateKey)}
                className={`h-[115px] border-r border-gray-100 px-2 py-1 relative text-left hover:bg-blue-50/40 transition last:border-r-0
                ${!dayInfo.current ? "bg-gray-50 text-gray-400" : "bg-white"}`}
              >
                <span
                  className={`absolute top-2 right-2 text-xs px-1.5 py-[1px] rounded font-medium
                  ${isToday ? "bg-blue-500 text-white" : "text-gray-700"}`}
                >
                  {dayInfo.day.toString().padStart(2, "0")}
                </span>

                <div className="mt-6 space-y-1 overflow-hidden">
                  {dateNoteCategories.length > 0 && (
                    <div className="flex items-center gap-1 justify-end">
                      {dateNoteCategories.slice(0, 4).map((category) => (
                        <span
                          key={category.key}
                          className={`h-2.5 w-2.5 rounded-full ${category.dot}`}
                          title={category.label}
                        />
                      ))}
                    </div>
                  )}

                  {dayTasks.slice(0, 1).map((task, idx) => (
                    <div
                      key={idx}
                      className="bg-orange-400 text-white text-[10px] px-2 py-[2px] rounded truncate"
                    >
                      {task.title}
                    </div>
                  ))}

                  {dayTasks.length > 1 && (
                    <div className="text-[10px] text-gray-500">
                      +{dayTasks.length - 1} more tasks
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="h-4 bg-white" />
        <div className="border-b border-gray-200" />
      </div>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={() => navigate("/dashboard/kanban")}
          className="bg-[#4253C7] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#3645A8] transition"
        >
          Open Kanban Board
        </button>
      </div>

      {selectedDate && (
        <div
          className="fixed inset-0 bg-black/20 z-50 flex justify-end items-start"
          onClick={closeNotes}
        >
          <div
            className="relative h-[1024px] max-h-screen w-[484px] max-w-full bg-white overflow-y-auto shadow-2xl font-[Poppins]"
            style={{ fontFamily: "Poppins, sans-serif" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close notes"
              className="absolute right-8 top-8 h-[33px] w-[33px] flex items-center justify-center text-black/50 hover:text-black"
              onClick={closeNotes}
            >
              <X size={33} strokeWidth={2.4} />
            </button>

            <div className="mt-[93px] ml-8 flex h-[59px] w-[420px] items-end justify-between">
              <div>
                <p
                  className="h-[21px] text-[14px] font-normal leading-[21px] text-black"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "21px",
                  }}
                >
                  Notes for
                </p>
                <h2
                  className="mt-2 h-[30px] text-[20px] font-medium leading-[30px] text-black"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "20px",
                    fontWeight: 500,
                    lineHeight: "30px",
                  }}
                >
                  {formatDisplayDate(selectedDate)}
                </h2>
              </div>

              <div className="mb-0 flex h-10 w-[46px] items-center justify-center rounded-lg bg-[#F1F5F9] text-[#3B82F6]">
                <Icons.Calendar/>
              </div>
            </div>

            <div className="relative ml-8 mt-10 h-[165px] w-[420px] rounded-lg bg-[#F1F5F9]">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Write a note....."
                className="absolute left-6 top-6 h-[94px] w-[348px] resize-none bg-transparent text-[14px] font-normal leading-[21px] text-gray-800 outline-none placeholder:text-[#7F7F7F]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "21px",
                }}
              />
              <button
                type="button"
                aria-label="Save note"
                onClick={handleAddNote}
                disabled={!noteText.trim()}
                className="absolute bottom-3 right-3 h-6 w-6 text-black disabled:text-gray-300"
              >
                <Icons.Tick />
              </button>
            </div>

            <div className="ml-8 mt-16 w-[420px]">
         <div className="flex items-start justify-between relative">
  <h3
    className="relative h-[23px] w-[60px] text-[14px] font-normal leading-[21px] text-black after:absolute after:left-px after:top-[21px] after:h-0.5 after:w-[25px] after:rounded-xl after:bg-[#4379FE]"
    style={{
      fontFamily: "Poppins, sans-serif",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "21px",
    }}
  >
    All Notes
  </h3>

  <div className="relative">
    <button
      type="button"
      aria-label="Show note categories"
      onClick={() => setShowLegend((prev) => !prev)}
      className="flex h-6 w-6 items-center justify-center text-[#666666] hover:text-gray-800"
    >
      <Info size={20} strokeWidth={1.5} />
    </button>

    {showLegend && (
      <>
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowLegend(false)}
        />
        <div className="absolute right-0 top-8 z-20 w-[137px] rounded-lg border border-black/[0.22] bg-white p-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
          {[
            { label: "Work", dot: "bg-[#4379FE]" },
            { label: "Meetings", dot: "bg-[#FFAE00]" },
            { label: "Personal", dot: "bg-[#22C55E]" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2 py-1 px-1">
              <span className={`h-2 w-2 shrink-0 rounded-full ${item.dot}`} />
              <span
                className="text-[12px] font-normal capitalize text-black"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
</div>

              {notesLoading && (
                <p className="mt-4 text-sm text-gray-400">Loading notes...</p>
              )}
              {notesError && (
                <p className="mt-4 text-sm text-red-500">{notesError}</p>
              )}

              <div className="mt-5 space-y-5 pb-8">
                {!notesLoading && selectedNotes.length === 0 && (
                  <div
                    className="w-[420px] rounded-lg border border-dashed border-gray-200 p-8 text-center text-[14px] text-gray-500"
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "21px",
                    }}
                  >
                    No notes yet.
                  </div>
                )}

                {selectedNotes.map((note) => {
                  const category = getCategoryForText(note.text);
                  const isEditing = editingNoteId === note._id;

                  return (
                    <div
                      key={note._id}
                      className={`relative flex h-auto min-h-[96px] w-[420px] flex-col rounded-lg ${category.bg} px-6 pb-5 pt-6`}
                    >
                      <div className="absolute right-5 top-5">
                        <button
                          type="button"
                          aria-label="Note actions"
                          className="flex h-3 w-3 items-center justify-center rounded text-black"
                          onClick={() =>
                            setActiveNoteMenu(
                              activeNoteMenu === note._id ? null : note._id,
                            )
                          }
                        >
                          <MoreVertical size={12} strokeWidth={3} />
                        </button>

                        {activeNoteMenu === note._id && (
                          <>
                            <div
                              className="fixed inset-0 z-[9]"
                              onClick={() => setActiveNoteMenu(null)}
                            />
                            <div className="absolute right-1 top-3 z-10 h-10 w-[52px] overflow-hidden rounded bg-white text-[8px] leading-3 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
                              <button
                                type="button"
                                className="flex h-5 w-full items-center px-1 text-left text-black hover:bg-gray-50"
                                style={{
                                  fontFamily: "Poppins, sans-serif",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  lineHeight: "12px",
                                }}
                                onClick={() => handleStartEdit(note)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="flex h-5 w-full items-center border-t border-black/[0.03] px-1 text-left text-red-500 hover:bg-red-50"
                                style={{
                                  fontFamily: "Poppins, sans-serif",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  lineHeight: "12px",
                                }}
                                onClick={() => handleDeleteNote(note._id)}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="pr-12">
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            className="w-full min-h-[90px] resize-y rounded-md bg-white/70 p-3 text-[14px] leading-[21px] text-gray-800 outline-none"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "21px",
                            }}
                          />
                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveEdit(note._id)}
                              disabled={!editingText.trim()}
                              className="inline-flex items-center gap-2 rounded-md bg-blue-500 px-3 py-2 text-sm text-white disabled:bg-gray-300"
                            >
                              <Check size={15} />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingNoteId(null);
                                setEditingText("");
                              }}
                              className="rounded-md bg-white px-3 py-2 text-sm text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-4 pr-[65px]">
                            <span
                              className={`mt-2 h-2 w-2 shrink-0 rounded-full ${category.dot}`}
                            />
                            <p
                              className={`max-w-[307px] whitespace-pre-wrap break-words text-[14px] font-normal leading-[21px] ${category.text}`}
                              style={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "21px",
                              }}
                            >
                              {note.text}
                            </p>
                          </div>
                          <p
                            className="mt-4 pl-8 text-[10px] font-normal leading-[15px] text-[#7F7F7F]"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "10px",
                              fontWeight: 400,
                              lineHeight: "15px",
                            }}
                          >
                            {formatTime(note.createdAt)}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWork;